import { create } from "zustand";
import {
  GetFieldStateType,
  GetValuesType,
  UseSignUpFormActionsType,
  UseSignUpFormStateType,
} from "../../SignUpFormTypes/signUpFormTypes";
import {
  signUpEmailStepCompleted,
  signUpStepCompleted,
} from "../signUpStepCompleted";

const initialState: UseSignUpFormStateType = {
  currentStep: 0,
  allowNext: false,
  steps: [],
  lastStepCta: "",
  startTime: Date.now(),
};

/**
 * ### Store Methods
 * - `getCurrentStep`  -> returns current step data
 * - `getIsFirstStep` -> returns a boolean
 * - `getIsLastStep` -> returns a boolean
 * - `next` -> on click, increases currentStep value.
 * - `prev` -> on click, decreases currentStep value.
 * - `setAllowNext` -> handles logic to allow users to continue to the next step
 * - `setSteps` -> handles logic for storing all form steps.
 */
export const useSignUpFormState = create<
  UseSignUpFormStateType & UseSignUpFormActionsType
>()((set, get) => ({
  ...initialState,
  getCurrentStep: () => {
    const { currentStep, steps } = get();

    return steps[currentStep];
  },
  getIsFirstStep: () => {
    const { currentStep } = get();

    return currentStep === 0;
  },
  getIsLastStep: () => {
    const { currentStep, steps } = get();

    return currentStep === steps.length - 1;
  },
  next: (ctaText: string, email: string) => {
    set(state => {
      if (state.currentStep >= state.steps.length - 1) {
        return {
          currentStep: state.currentStep,
        };
      }

      signUpStepCompleted({
        fields: state.steps[state.currentStep].fields,
        step: state.currentStep + 1,
      });

      const currentStep = state.currentStep + 1;

      signUpEmailStepCompleted(state, ctaText, email);

      return {
        allowNext: false,
        currentStep,
      };
    });
  },
  prev: () => {
    set(state => {
      if (state.currentStep <= 0) {
        return {
          currentStep: state.currentStep,
        };
      }

      const currentStep = state.currentStep - 1;

      return {
        allowNext: true,
        currentStep,
      };
    });
  },
  setAllowNext: (
    getValues: GetValuesType,
    getFieldState: GetFieldStateType
  ) => {
    const { getCurrentStep, allowNext } = get();

    const step = getCurrentStep();

    const shouldChangeAllowNext = step?.fields.every(item => {
      const fieldName = item?.field;
      const currentFieldValue = getValues(fieldName);
      const { error, invalid } = getFieldState("email");
      if (
        currentFieldValue &&
        typeof currentFieldValue === "string" &&
        currentFieldValue.trim() &&
        !error &&
        !invalid
      ) {
        return true;
      }

      return false;
    });

    if (shouldChangeAllowNext !== allowNext) set({ allowNext });
  },
  setSteps: steps => {
    set({ steps });
  },
  setLastStepCta: (ctaLastStep?: string) => {
    if (ctaLastStep) {
      set({ lastStepCta: ctaLastStep });
    } else {
      set({ lastStepCta: "Start My Free Trial Now" });
    }
  },
}));
